import React, { useEffect } from 'react';

// Public privacy policy page, reachable at /privacy without signing in.
// Required to be publicly accessible for Google Play. Styled to match the
// public Narratoria storefront: dark slate background with the Cormorant
// Garamond serif and the gold wordmark.
export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy | Narratoria';
    const id = 'cormorant-garamond-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  // Gold wordmark, matching the landing page without using amber- classes.
  const gold = {
    backgroundImage: 'linear-gradient(to bottom, #fde9b8, #d9a54b, #b07d2b)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0d1117] to-slate-950 text-slate-200"
      style={serif}
    >
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <header className="mb-10 text-center">
          <a
            href="/reader"
            className="text-4xl sm:text-5xl font-semibold tracking-wide"
            style={{ ...serif, ...gold }}
          >
            NARRATORIA
          </a>
          <h1 className="mt-6 text-3xl sm:text-4xl font-semibold text-white">
            Privacy Policy
          </h1>
          <p className="mt-2 text-slate-400">Last updated: 29 June 2026</p>
        </header>

        <article
          className="prose prose-invert prose-slate max-w-none
            prose-headings:font-semibold prose-headings:text-white
            prose-h2:mt-10 prose-h2:text-2xl
            prose-p:leading-relaxed prose-li:leading-relaxed
            prose-a:text-teal-400 hover:prose-a:text-teal-300
            prose-strong:text-white"
        >
          <h2>1. Who we are</h2>
          <p>
            This privacy policy explains how Narratoria Ltd ("Narratoria", "we",
            "us") collects and uses your personal data when you use the
            Narratoria app and storefront (the "Service").
          </p>
          <p>
            Narratoria Ltd is the data controller responsible for your personal
            data. We are a company registered in Scotland (company number
            SC880318).
          </p>
          <p>
            If you have any questions about this policy or your personal data,
            you can contact us at{' '}
            <a href="mailto:admin@narratoria.com">admin@narratoria.com</a>.
          </p>

          <h2>2. What this policy covers</h2>
          <p>
            This policy applies to the personal data we collect from you when you
            use the Narratoria app and storefront, including when you create an
            account, rent or buy audiobooks, and listen to them. It does not
            cover third-party services that the Service links to, which have
            their own privacy policies.
          </p>

          <h2>3. The information we collect</h2>
          <p>
            <strong>Account information.</strong> When you create an account, we
            collect your name and email address. Sign-in and passwords are
            handled securely by Google Firebase Authentication, and we never see
            or store your password.
          </p>
          <p>
            <strong>Payment and transaction information.</strong> When you rent
            or buy an audiobook, your payment is processed by Stripe. We do not
            see or store your full card details; these are handled directly by
            Stripe as our payment processor. We keep records of your
            transactions, such as the items you rented or bought, the amounts,
            and the dates.
          </p>
          <p>
            <strong>Audiobook and listening information.</strong> We store
            information about the audiobooks you rent or access, including your
            rental status and expiry dates and whether you have downloaded a
            title. When you download an audiobook for offline listening, the
            audio is stored in encrypted form on your own device, linked to your
            account. Once your rental expires the downloaded audio can no longer
            be played, because it can only be decrypted while the rental is
            active. You can delete a download from your device at any time.
          </p>
          <p>
            <strong>Technical information.</strong> To run and protect the
            Service, our infrastructure providers process basic technical
            information such as your IP address, device type, and the dates and
            times you access the Service. We do not use this information for
            advertising or behavioural tracking.
          </p>
          <p>
            <strong>Notification information.</strong> If you use our mobile app
            and allow notifications when your device asks, we register a device
            notification token (a Firebase Cloud Messaging token) and store it
            against your account. We use it to send you service notifications
            about your rentals, such as reminders that a rental is about to
            expire. You can turn notifications off at any time in your device
            settings, which withdraws this permission.
          </p>
          <p>
            <strong>What we do not collect.</strong> We do not use third-party
            analytics or crash-reporting tools, and we do not send marketing or
            newsletter emails through the Service.
          </p>

          <h2>4. How we use your information, and our lawful bases</h2>
          <p>We use your personal data to:</p>
          <ul>
            <li>
              create and manage your account, and let you sign in (lawful basis:
              performance of our contract with you);
            </li>
            <li>
              process your rentals and purchases, deliver audiobooks, and manage
              download access and expiry (performance of our contract with you);
            </li>
            <li>
              send you account and service emails, such as sign-in verification
              (handled by Firebase Authentication) and purchase receipts, order
              confirmations and ARC confirmations (sent by email through our
              email provider over SMTP), for the performance of our contract
              with you and our legitimate interests in running the Service;
            </li>
            <li>
              send you service notifications about your rentals, such as expiry
              reminders, where you have allowed notifications on your device
              (consent, which you give through your device's notification
              permission and can withdraw at any time);
            </li>
            <li>
              keep proper financial and transaction records (compliance with our
              legal obligations); and
            </li>
            <li>
              protect the Service against fraud and misuse, and keep it secure
              (our legitimate interests in operating a safe and reliable
              service).
            </li>
          </ul>
          <p>
            Where we rely on legitimate interests, we make sure those interests
            are not overridden by your rights.
          </p>

          <h2>5. Who we share your information with</h2>
          <p>We share personal data only where necessary, with:</p>
          <ul>
            <li>
              <strong>Google (Firebase):</strong> our infrastructure provider,
              which hosts the Service, stores your account and rental data,
              handles authentication, and delivers notifications through Firebase
              Cloud Messaging;
            </li>
            <li>
              <strong>Email delivery (Google — Gmail / Google Workspace
              SMTP):</strong> the mail service we use to send our transactional
              emails, such as receipts and order and ARC confirmations; it
              receives the recipient email address and the message content;
            </li>
            <li>
              <strong>Stripe:</strong> our payment processor, which handles your
              payments; and
            </li>
            <li>
              <strong>others where required:</strong> for example, if we are
              required to disclose information by law, or to protect our rights,
              property, or safety, or those of our users.
            </li>
          </ul>
          <p>
            These providers act as our processors and are only permitted to use
            your data to provide their services to us. We do not sell your
            personal data.
          </p>

          <h2>6. International transfers</h2>
          <p>
            Some of our providers, including Google and Stripe, may process your
            personal data outside the United Kingdom. Where they do, we rely on
            appropriate safeguards, such as the UK's adequacy regulations or
            standard contractual clauses, to make sure your data is protected to
            a similar standard as it is in the UK.
          </p>

          <h2>7. How long we keep your information</h2>
          <p>
            We keep your account information for as long as your account is
            active, and for up to 12 months after it is closed, in case you wish
            to reopen it or in case of any queries.
          </p>
          <p>
            We keep transaction and financial records for six years, to meet our
            legal and accounting obligations in the UK.
          </p>
          <p>
            Downloaded audio stays on your device until you delete it or
            uninstall the app. Once your rental expires it can no longer be
            played, because it can only be decrypted while the rental is active.
          </p>

          <h2>8. How we protect your information</h2>
          <p>
            We take the security of your data seriously. Sign-in and passwords
            are handled by Google Firebase Authentication, payments are handled
            by Stripe under their PCI-compliant systems, and audiobooks
            downloaded for offline listening are stored on your device using
            strong encryption (AES-256-GCM) and tied to your account. While no
            system can be guaranteed completely secure, we use appropriate
            technical and organisational measures to protect your personal data.
          </p>

          <h2>9. Your rights</h2>
          <p>Under UK data protection law, you have the right to:</p>
          <ul>
            <li>access the personal data we hold about you;</li>
            <li>ask us to correct inaccurate or incomplete data;</li>
            <li>ask us to delete your data in certain circumstances;</li>
            <li>ask us to restrict or object to how we use your data;</li>
            <li>ask for a copy of your data in a portable format; and</li>
            <li>withdraw any consent you have given, at any time.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:admin@narratoria.com">admin@narratoria.com</a>. We
            will respond within the time limits required by law.
          </p>

          <h2>10. Cookies and local storage</h2>
          <p>
            The Service uses essential cookies and local storage to keep you
            signed in and to make the app and storefront work, including storing
            downloaded audiobooks on your device for offline listening. We do
            not use cookies for advertising or tracking.
          </p>

          <h2>11. Children</h2>
          <p>
            The Service is intended for adults aged 18 and over, and is not
            directed at anyone under 18. We do not knowingly collect personal
            data from anyone under 18. If you believe someone under 18 has
            provided us with personal data, please contact us and we will remove
            it.
          </p>

          <h2>12. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. When we do, we will
            change the "last updated" date at the top, and where the changes are
            significant we will take reasonable steps to let you know.
          </p>

          <h2>13. How to contact us, and your right to complain</h2>
          <p>
            If you have any questions about this policy or wish to exercise your
            rights, contact us at{' '}
            <a href="mailto:admin@narratoria.com">admin@narratoria.com</a>.
          </p>
          <p>
            If you are not happy with how we have handled your personal data, you
            have the right to complain to the Information Commissioner's Office
            (ICO), the UK's data protection regulator, at{' '}
            <a href="https://ico.org.uk" target="_blank" rel="noreferrer">
              ico.org.uk
            </a>
            .
          </p>
        </article>

        <footer className="mt-14 border-t border-slate-800 pt-6 text-center text-sm text-slate-500">
          Narratoria Ltd, registered in Scotland (company number SC880318).
        </footer>
      </div>
    </div>
  );
}
